import { useModal } from "@logora/debate.dialog.modal";
import { Select } from "@logora/debate.input.select";
import { TextInput } from "@logora/debate.input.text_input";
import { DocumentIcon } from "@logora/parliament.icons.regular_icons";
import { AmendmentIcon } from "@logora/parliament.icons.regular_icons";
import { DossierIcon } from "@logora/parliament.icons.regular_icons";
import { SpeechIcon } from "@logora/parliament.icons.regular_icons";
import { OrganeIcon } from "@logora/parliament.icons.regular_icons";
import { QuestionIcon } from "@logora/parliament.icons.regular_icons";
import { ReunionIcon } from "@logora/parliament.icons.regular_icons";
import { VoteIcon } from "@logora/parliament.icons.regular_icons";
import { ActeurIcon } from "@logora/parliament.icons.regular_icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
	Box,
	Button,
	FormControlLabel,
	SnackbarContent,
	Switch,
	Modal,
	Paper,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import CreatableSelect from "react-select/creatable";
import styles from "./AlertForm.module.scss";
import AlertSourceField from "./AlertSourceField";
import { customCreatableSelectStyles } from "./customSelectStyles";
import useAlertFormValidator from "./useAlertFormValidator";

const SOURCE_OPTIONS = [
	{ name: "dossiers", value: "dossiers", text: "Dossiers" },
	{ name: "amendements", value: "amendements", text: "Amendements" },
	{ name: "reunions", value: "reunions", text: "Réunions" },
	{ name: "interventions", value: "interventions", text: "Interventions" },
	{ name: "questions", value: "questions", text: "Questions" },
	/*
	{ name: "documents", value: "documents", text: "Documents" },
	{ name: "organes", value: "organes", text: "Organes" },
	 */
];

const FRENQUENCY_OPTIONS = [
	{ name: "hourly", value: "hourly", text: "Horaire" },
	{ name: "daily", value: "daily", text: "Quotidienne" },
	{ name: "weekly", value: "weekly", text: "Hebdomadaire" },
	{ name: "monthly", value: "monthly", text: "Mensuelle" },
];

// Fix conflicts with drawer
const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	maxWidth: '650px',
	width: '100%',
	bgcolor: 'background.paper',
	borderRadius: '8px',
	boxShadow: 24,
	p: 3,
	outline: 'none',
	zIndex: 1500,
};

export const AlertModal = ({
	record,
	targetId,
	targetClass,
	onCreateAlert,
}) => {
	const [isObjectAlert, setIsObjectAlert] = useState(targetId && targetClass);
	const [keywordsOpen, setKeywordsOpen] = useState(!(targetId && targetClass));
	const { hideModal } = useModal();
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const refresh = useRefresh();

	
	const [formData, setFormData] = useState({
		name: "",
		keywords: [],
		active: true,
		searchQuery: false,
		sources: SOURCE_OPTIONS.map((option) => option.value),
		frequency: "daily",
		receiveByEmail: true,
		receiveByNotification: false,
	});
	
	const { errors, validateForm } = useAlertFormValidator(formData, isObjectAlert);
	
	const successNotification = (message) => (
		<SnackbarContent
			autoHideDuration={3000}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			message={message}
			sx={{ backgroundColor: "#2E7D32", color: "#FFF" }}
		/>
	);

	useEffect(() => {
		if (record) {
			setFormData(() => {
				return {
					name: record.name,
					keywords: record.keywords?.map((k) => ({
						label: k.name,
						value: k.name,
					})),
					active: record.active,
					searchQuery: record.searchQuery,
					sources: record.sources?.map((s) => s.name),
					frequency: record.frequency,
					receiveByEmail: record.email_notifications,
					receiveByNotification: record.push_notifications,
				};
			});

			setIsObjectAlert(record.target_id && record.target_class);
			setKeywordsOpen(!(record.target_id && record.target_class));
		}
	}, [record]);

	const getIcon = (name) => {
		const icons = {
			documents: <DocumentIcon width={18} height={18} />,
			amendements: <AmendmentIcon width={18} height={18} />,
			dossiers: <DossierIcon width={18} height={18} />,
			scrutins: <VoteIcon width={18} height={18} />,
			interventions: <SpeechIcon width={18} height={18} />,
			organes: <OrganeIcon width={18} height={18} />,
			questions: <QuestionIcon width={18} height={18} />,
			réunions: <ReunionIcon width={18} height={18} />,
			votes: <VoteIcon width={18} height={18} />,
			acteurs: <ActeurIcon width={18} height={18} />,
		};
		return icons[name.toLowerCase()] || null;
	};

	const handleChange = (event) => {
		const { name, value, checked, type } = event.target;
		setFormData((prevData) => ({
			...prevData,
			[type === "checkbox" ? name : name]:
				type === "checkbox" ? checked : value,
		}));
	};

	const handleSourceToggle = (sourceValue) => {
		setFormData((prevData) => {
			const isSelected = prevData.sources.includes(sourceValue);
			return {
				...prevData,
				sources: isSelected
					? prevData.sources.filter((value) => value !== sourceValue)
					: [...prevData.sources, sourceValue],
			};
		});
	};

	const handleFrequencyChange = (option) => {
		setFormData((prevData) => ({
			...prevData,
			frequency: option.value,
		}));
	};

	const handleKeywordsChange = (selectedOptions) => {
		const cleanedValues = selectedOptions
			? selectedOptions.map((option) => ({
					label: option.value.trim().toLowerCase(),
					value: option.value.trim().toLowerCase(),
				}))
			: [];
		setFormData((prevData) => ({
			...prevData,
			keywords: cleanedValues,
		}));
	};

	const getTargetUrl = (formData) => {
		const getUidMapping = (targetClass) => {
			const uidMappings = {
				Acteur: "acteurRefUid",
				Amendement: "amendementRefUid",
				Dossier: "dossierRefUid",
				Scrutin: "scrutinRefUid",
				Document: "documentRefUid",
				Organe: "organeRefUid",
				Question: "questionRefUid",
				Reunion: "reunionRefUid",
			};
			return uidMappings[targetClass] || null;
		};

		const buildQueryString = (keywords) =>
			keywords?.map((k) => k.value).join("+") || "";

		const currentTargetId = targetId || record?.target_id;
		const currentTargetClass = targetClass || record?.target_class;
		const currentTargetUidMapping = getUidMapping(currentTargetClass);

		if (currentTargetId && currentTargetClass && currentTargetUidMapping) {
			return `${window.location.origin}/#/parliament/${currentTargetClass.toLowerCase()}s/?${currentTargetUidMapping}=${currentTargetId}&query=${buildQueryString(formData.keywords)}`;
		}

		const defaultSource = formData.sources?.[0] || "dossiers";
		return `${window.location.origin}/#/parliament/${defaultSource}?query=${buildQueryString(formData.keywords)}`;
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		const submitData = {
			name: formData.name,
			keywords: formData.keywords.map((k) => k.value),
			active: formData.active,
			searchQuery: formData.searchQuery,
			sources: formData.sources,
			target_id: targetId,
			target_class: targetClass,
			target_url: getTargetUrl(formData),
			frequency: formData.frequency,
			email_notifications: formData.receiveByEmail,
			push_notifications: formData.receiveByNotification,
		};

		if (validateForm()) {
			if (record) {
				dataProvider
					.update("alerts", {
						id: record.id,
						data: {
							id: record.id,
							...submitData,
						},
					})
					.then(() => {
						hideModal();
						refresh();
						notify(successNotification("Alerte modifiée avec succès"));
					})
					.catch(() => {
						console.log("Une erreur est survenue");
					});
			} else {
				dataProvider
					.create("alerts", {
						data: submitData,
					})
					.then((res) => {
						if (res?.data) {
							onCreateAlert?.(res.data);
						}
						hideModal();
						refresh();
						notify(successNotification("Alerte créée avec succès"));
					})
					.catch(() => {
						console.log("Une erreur est survenue");
					});
			}
		} else {

		}
	};

	const handleDelete = () => {
		dataProvider
			.delete("alerts", {
				id: record.id,
			})
			.then(() => {
				hideModal();
				refresh();
				notify(successNotification("Alerte supprimée avec succès"));
			})
			.catch(() => {
				console.log("Une erreur est survenue");
			});
	};
	
	return (
		<Modal
			open={true}
			onClose={hideModal}
			aria-labelledby="alert-modal-title"
			aria-describedby="alert-modal-description"
			disableEnforceFocus={false}
			disableAutoFocus={false}
			disableRestoreFocus={false}
		>
			<Paper sx={modalStyle}>
				<form className={styles.formContainer}>
					<Box
						display={"flex"}
						flexDirection={"row"}
						justifyContent={"space-between"}
						marginBottom={"1em"}
					>
					<h1 className={styles.formTitle}>
						{record ? "Modifier l'alerte" : "Nouvelle alerte"}
					</h1>
					<FormControlLabel
						label="Alerte active"
						control={
							<Switch
								checked={formData.active}
								onChange={handleChange}
								name="active"
								className={styles.switchLabel}
							/>
						}
					/>
				</Box>
				<Box display={"flex"} flexDirection={"column"} gap={"1em"} mb={"20px"}>
					<Box display={"flex"} flexDirection={"column"}>
						<div className={styles.alertOptions}>Nom de l'alerte</div>
						<TextInput
							name="name"
							value={formData.name}
							onChange={handleChange}
							className={styles.textInput}
							inputClass={styles.textInputClass}
						/>
						{errors.name && <span className={styles.errorsContainer}>{errors.name}</span>}
					</Box>
					<Box display={"flex"} flexDirection={"column"}>
						<div
							className={`${styles.keywordsOptions} ${
								keywordsOpen ? styles.keywordsOpen : styles.keywordsClosed
							}`}
							onClick={() => setKeywordsOpen((prev) => !prev)}
						>
							{isObjectAlert &&
								(!keywordsOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />)}
							Mots-clés {isObjectAlert && <i>(Optionnel)</i>}
						</div>
						{keywordsOpen && (
							<CreatableSelect
								styles={customCreatableSelectStyles}
								components={{ DropdownIndicator: null }}
								isMulti
								isClearable={false}
								value={formData.keywords}
								onChange={handleKeywordsChange}
								placeholder="Ajouter des mots-clés..."
							/>
						)}
						{errors.keywords && <span className={styles.errorsContainer}>{errors.keywords}</span>}
					</Box>
				</Box>
				<div className={styles.selectWrapper}>
					{record?.target_id && record?.target_class && (
						<Box
							display={"flex"}
							flexDirection={"column"}
							justifyContent={"center"}
							alignItems={"flex-start"}
							gap={"1em"}
							mb={"1em"}
						>
							<div className={styles.alertOptions}>Source principale</div>
							<div className={styles.alertOptions}>
								<AlertSourceField
									targetId={targetId || record?.target_id}
									targetClass={targetClass || record?.target_class}
								/>
							</div>
						</Box>
					)}
					<>
						<Box
							display={"flex"}
							flexDirection={"row"}
							justifyContent={"flex-start"}
							alignItems={"center"}
							gap={"1em"}
							mb={"1em"}
						>
							<div className={styles.alertOptions}>Sources</div>
							{formData?.sources?.length >= 2 && (
								<span
									className={styles.deselectOptions}
									onClick={() =>
										setFormData((prevData) => ({ ...prevData, sources: [] }))
									}
								>
									Tout déselectionner
								</span>
							)}
							{formData?.sources?.length === 0 && (
								<span
									className={styles.deselectOptions}
									onClick={() =>
										setFormData((prevData) => ({
											...prevData,
											sources: SOURCE_OPTIONS.map((option) => option.value),
										}))
									}
								>
									Tout sélectionner
								</span>
							)}
						</Box>
						<Box className={styles.sourceButtons}>
							{SOURCE_OPTIONS.map((option) => (
								<div
									key={option.value}
									onClick={() => handleSourceToggle(option.value)}
									className={`${styles.sourceButton} ${
										formData.sources.includes(option.value)
											? styles.sourceButtonSelected
											: ""
									}`}
								>
									{getIcon(option.text)} {option.text}
								</div>
							))}
						</Box>
						{errors.sources && <span className={styles.errorsContainer}>{errors.sources}</span>}
					</>
				</div>
				<div className={styles.selectWrapper}>
					<div className={styles.alertOptions}>Fréquence</div>
					<Select
						label="Fréquence"
						className={styles.selectInput}
						options={FRENQUENCY_OPTIONS}
						defaultOption={formData.frequency}
						onChange={handleFrequencyChange}
					/>
				</div>
				{/* <div className={styles.alertOptions}>Réception des alertes</div>
				<Box display={"flex"} flexDirection={"column"}>
					<FormControlLabel
						label="Par mail"
						control={
							<Switch
								checked={formData.receiveByEmail}
								onChange={handleChange}
								name="receiveByEmail"
								className={styles.switchLabel}
							/>
						}
					/>
				</Box> */}
				<div className={styles.buttons}>
					{record && (
						<Button
							variant="outlined"
							type="button"
							className={styles.deleteButton}
							onClick={handleDelete}
						>
							Supprimer
						</Button>
					)}
					<Button
						variant="outlined"
						type="button"
						className={styles.cancelButton}
						onClick={hideModal}
					>
						Annuler
					</Button>
					<Button
						variant="outlined"
						type="button"
						className={styles.submitButton}
						onClick={handleSubmit}
					>
						Valider
					</Button>
				</div>
			</form>
			</Paper>
		</Modal>
	);
};
